"use client"

import React, { useState, useEffect, useCallback, useRef } from "react"
import { Activity, RefreshCw } from "lucide-react"
import { Button } from "../ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip"
import { cn } from "../lib/utils"

/**
 * Status Pulse — a header control that shows the live connectivity of the
 * user's device to the AskFinz backend services. Modeled on <WorldClock>: a
 * ghost icon trigger with an overall status dot, opening a card that lists
 * each service with its own dot + round-trip latency.
 *
 * Method (Option A, frontend-only): the browser pings each service's public
 * `/api/health`. Cross-origin services are probed with `mode: "no-cors"` so
 * CORS never produces a false negative — a resolved request (even an opaque
 * response) means the host answered = reachable; a rejected/aborted request
 * means unreachable. The same-origin app backend is probed normally.
 *
 * To widen coverage to a server-verified "all services" view later, swap the
 * SERVICES pings for a single aggregator endpoint (e.g.
 * `account-api /api/status/services`) without touching the UI.
 */

export type PulseStatus = "up" | "slow" | "down" | "checking"

interface ServiceDef {
  name: string
  url: string
  /** Same-origin probe — read the real HTTP status instead of an opaque ping. */
  sameOrigin?: boolean
}

// Curated set of key services. Public domains are used so the probe works in
// prod; `/api/health` is a public path on every app (see auth-middleware).
const SERVICES: ServiceDef[] = [
  { name: "This app", url: "/api/health", sameOrigin: true },
  { name: "Account & Auth", url: "https://account.askfinz.ai/api/health" },
  { name: "Search", url: "https://search.askfinz.ai/api/health" },
  { name: "Map & Geo", url: "https://map.askfinz.ai/api/health" },
  { name: "Chat", url: "https://chat.askfinz.ai/api/health" },
]

const SLOW_MS = 1500
const TIMEOUT_MS = 6000
const REFRESH_MS = 30000

interface ServiceResult {
  name: string
  status: PulseStatus
  latencyMs: number | null
}

async function pingService(svc: ServiceDef): Promise<ServiceResult> {
  const start = (typeof performance !== "undefined" ? performance.now() : 0)
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS)
  try {
    await fetch(svc.url, {
      method: "GET",
      cache: "no-store",
      mode: svc.sameOrigin ? "cors" : "no-cors",
      signal: ctrl.signal,
      // no-cors opaque responses can't expose status; a resolved promise is
      // sufficient proof the host is reachable.
    })
    clearTimeout(timer)
    const latency = Math.round((typeof performance !== "undefined" ? performance.now() : 0) - start)
    return { name: svc.name, status: latency > SLOW_MS ? "slow" : "up", latencyMs: latency }
  } catch {
    clearTimeout(timer)
    return { name: svc.name, status: "down", latencyMs: null }
  }
}

function worstStatus(results: ServiceResult[]): PulseStatus {
  if (results.length === 0) return "checking"
  if (results.some((r) => r.status === "down")) return "down"
  if (results.some((r) => r.status === "slow")) return "slow"
  return "up"
}

const DOT: Record<PulseStatus, string> = {
  up: "bg-green-500",
  slow: "bg-amber-500",
  down: "bg-red-500",
  checking: "bg-muted-foreground/40 animate-pulse",
}

const LABEL: Record<PulseStatus, string> = {
  up: "All systems operational",
  slow: "Degraded — slow responses",
  down: "Connection issue",
  checking: "Checking…",
}

export interface StatusPulseProps {
  className?: string
}

export function StatusPulse({ className }: StatusPulseProps) {
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<ServiceResult[]>([])
  const [checking, setChecking] = useState(false)
  const isMounted = useRef(true)

  const runChecks = useCallback(async () => {
    if (!isMounted.current) return
    setChecking(true)
    try {
      const next = await Promise.all(SERVICES.map(pingService))
      if (isMounted.current) setResults(next)
    } finally {
      if (isMounted.current) setChecking(false)
    }
  }, [])

  // Initial check after mount (deferred so SSR/first-client render match) +
  // periodic refresh.
  useEffect(() => {
    isMounted.current = true
    runChecks()
    const t = setInterval(runChecks, REFRESH_MS)
    return () => {
      isMounted.current = false
      clearInterval(t)
    }
  }, [runChecks])

  // Re-check immediately when the card is opened.
  useEffect(() => {
    if (open) runChecks()
  }, [open, runChecks])

  const overall: PulseStatus = checking && results.length === 0 ? "checking" : worstStatus(results)

  return (
    <TooltipProvider>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-9 w-9 rounded-full relative", className)}
                aria-label="Service status"
              >
                <Activity className="h-4 w-4" />
                <span
                  className={cn(
                    "absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[hsl(var(--header-footer-background))]",
                    DOT[overall],
                  )}
                  aria-hidden
                />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Service status</TooltipContent>
        </Tooltip>
        <PopoverContent
          className="w-[min(300px,90vw)] p-0 border bg-popover text-popover-foreground shadow-md mt-3"
          align="end"
          side="bottom"
          sideOffset={12}
        >
          <div className="flex items-center justify-between border-b bg-muted/30 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className={cn("h-2.5 w-2.5 rounded-full shrink-0", DOT[overall])} aria-hidden />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">Status Pulse</div>
                <div className="text-[11px] text-muted-foreground truncate">{LABEL[overall]}</div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={runChecks}
              disabled={checking}
              aria-label="Re-check"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", checking && "animate-spin")} />
            </Button>
          </div>
          <ul className="divide-y">
            {SERVICES.map((svc) => {
              const r = results.find((x) => x.name === svc.name)
              const status: PulseStatus = r?.status ?? "checking"
              return (
                <li key={svc.name} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={cn("h-2 w-2 rounded-full shrink-0", DOT[status])} aria-hidden />
                    <span className="truncate">{svc.name}</span>
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground shrink-0">
                    {status === "down"
                      ? "offline"
                      : status === "checking"
                        ? "…"
                        : r?.latencyMs != null
                          ? `${r.latencyMs} ms`
                          : ""}
                  </span>
                </li>
              )
            })}
          </ul>
          <div className="px-3 py-2 border-t text-[10px] text-muted-foreground">
            Device → backend connectivity · refreshes every 30s
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  )
}
